import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, RefreshCw, ArrowUpRight } from "lucide-react";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 12 questions, each with 4 options scored 0 (worst) → 3 (best). Max = 36.
const QUESTIONS = [
  { theme: "Process documentation", q: "How much of your program's day-to-day process is written down?",
    options: ["Barely any — it lives in people's heads", "Some ad-hoc notes and Google Docs", "Most workflows have some documentation", "Every key workflow has current SOPs"] },
  { theme: "Ownership & responsibilities", q: "Is it always clear who owns what?",
    options: ["Regular confusion about ownership", "Clear for major things, unclear for the rest", "Most areas have named owners", "Every function has a documented owner"] },
  { theme: "Access to information", q: "Can any team member find what they need without asking a specific person?",
    options: ["No — everything runs through one or two people", "Sometimes, if they know where to look", "Mostly yes, with some gaps", "Yes — information is centrally accessible"] },
  { theme: "Personal-inbox reliance", q: "How much critical work sits in personal inboxes?",
    options: ["Most decisions and approvals live in email threads", "A significant amount", "Some legacy items", "Very little — we use shared systems"] },
  { theme: "Spreadsheet dependency", q: "How dependent are you on personal spreadsheets that only one person understands?",
    options: ["Extremely — several are business-critical", "A few key ones exist", "Minor dependency", "Everything is in shared, documented systems"] },
  { theme: "Continuity planning", q: "If a key person left tomorrow, would the program keep running?",
    options: ["It would break", "It would struggle for weeks", "There would be a rough patch, then recovery", "Yes — succession is planned for" ] },
  { theme: "Staff coverage", q: "Is there coverage for every key role?",
    options: ["No — several single points of failure", "Coverage for major roles only", "Coverage for most, with some gaps", "Every key role has documented backup"] },
  { theme: "Approvals", q: "Where do approvals bottleneck?",
    options: ["Constantly — one or two people block everything", "Regularly on high-value items", "Occasionally", "Approvals rarely bottleneck"] },
  { theme: "Governance", q: "How well-defined is the governance of the program?",
    options: ["Ad-hoc — decisions get made informally", "Some structure but inconsistent", "Clear governance for major decisions", "Documented, tested and reviewed regularly"] },
  { theme: "Training & onboarding", q: "How quickly can a new team member get productive?",
    options: ["Months, and it depends on shadowing", "Several weeks", "A couple of weeks with support", "Days — we have real onboarding materials"] },
  { theme: "Stakeholder knowledge", q: "How well-documented are your stakeholder relationships?",
    options: ["Relationships live in one person's phone", "Some contact records exist", "Most stakeholders are documented", "CRM captures full history and next steps"] },
  { theme: "Operational risk", q: "Do you maintain a real risk register?",
    options: ["No", "A stale one exists somewhere", "Yes but rarely reviewed", "Live risk register, reviewed regularly"] },
];

export default function Diagnostic() {
  useDocumentMeta({
    title: "Program Resilience Diagnostic · 2-minute assessment — JJ Argent",
    description:
      "Twelve questions. Get your program's resilience tier, a short read on where you sit, and three actions to start this week. Free · 2 minutes · No signup.",
    path: "/diagnostic",
  });
  const [step, setStep] = useState(-1); // -1 intro, 0-11 questions, 12 capture, 13 result
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [capture, setCapture] = useState({ full_name: "", email: "", organisation: "" });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const pickAnswer = (val) => {
    const next = answers.slice();
    next[step] = val;
    setAnswers(next);
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) setStep((s) => s + 1);
      else setStep(QUESTIONS.length); // → capture
    }, 220);
  };

  const submit = async (withEmail = true) => {
    setSubmitting(true);
    setError(null);
    try {
      const body = { answers, ...(withEmail ? capture : {}) };
      const r = await axios.post(`${API}/diagnostics`, body);
      setResult(r.data);
      setStep(QUESTIONS.length + 1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setAnswers(Array(QUESTIONS.length).fill(null));
    setCapture({ full_name: "", email: "", organisation: "" });
    setResult(null);
    setStep(-1);
    setError(null);
  };

  const computeProgressPct = () => {
    if (step < 0) return 0;
    if (step >= QUESTIONS.length) return 100;
    return Math.round((step / QUESTIONS.length) * 100);
  };
  const pct = computeProgressPct();
  const totalSteps = QUESTIONS.length;

  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6] min-h-screen" data-testid="diagnostic-page">
      <section className="pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {step < 0 && (
            <IntroScreen onStart={() => setStep(0)} />
          )}

          {step >= 0 && step < QUESTIONS.length && (
            <>
              <ProgressBar current={step + 1} total={totalSteps} pct={pct} />
              <AnimatePresence mode="wait">
                <QuestionScreen
                  key={step}
                  index={step}
                  question={QUESTIONS[step]}
                  selected={answers[step]}
                  onPick={pickAnswer}
                  onBack={step > 0 ? () => setStep((s) => s - 1) : null}
                />
              </AnimatePresence>
            </>
          )}

          {step === QUESTIONS.length && (
            <CaptureScreen
              form={capture}
              onChange={setCapture}
              onSubmit={() => submit(true)}
              onSkip={() => submit(false)}
              submitting={submitting}
              error={error}
            />
          )}

          {step === QUESTIONS.length + 1 && result && (
            <ResultScreen result={result} onRestart={restart} />
          )}
        </div>
      </section>
    </main>
  );
}

function IntroScreen({ onStart }) {
  return (
    <div data-testid="diagnostic-intro">
      <p className="eyebrow">Diagnostic</p>
      <h1 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98] tracking-[-0.04em]">
        Would your program{" "}<br />
        <span className="text-[#D8A936]">survive without its best person?</span>
      </h1>
      <p className="mt-8 text-lg leading-relaxed text-[#A9A9A6] max-w-2xl">
        Twelve short questions. Two minutes. You'll get a resilience tier, a short read on where you sit,
        and three actions you can start on this week.
      </p>
      <p className="mt-4 text-sm text-[#A9A9A6] max-w-2xl italic">
        This is an initial indication only — not a formal professional assessment.
      </p>
      <button
        onClick={onStart}
        data-testid="diagnostic-start"
        className="btn-primary mt-10"
      >
        Start the diagnostic <ArrowRight size={14} />
      </button>
    </div>
  );
}

function ProgressBar({ current, total, pct }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between text-[10px] tracking-[0.24em] uppercase text-[#A9A9A6] mb-3">
        <span>Question {current} of {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1 bg-[#151517] border border-[#343437]">
        <motion.div
          className="h-full bg-[#D8A936]"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>
    </div>
  );
}

function QuestionScreen({ index, question, selected, onPick, onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      data-testid={`diagnostic-question-${index}`}
    >
      <p className="eyebrow">{question.theme}</p>
      <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight tracking-tight text-[#FAF9F6]">
        {question.q}
      </h2>
      <div className="mt-10 space-y-3">
        {question.options.map((opt, i) => (
          <button
            key={`opt-${index}-${i}`}
            onClick={() => onPick(i)}
            data-testid={`diagnostic-option-${index}-${i}`}
            className={`w-full text-left border p-5 flex items-center gap-4 transition-all duration-300 ${
              selected === i
                ? "border-[#D8A936] bg-[#D8A936]/10 text-[#FAF9F6]"
                : "border-[#343437] text-[#A9A9A6] hover:border-[#FAF9F6] hover:text-[#FAF9F6]"
            }`}
          >
            <span className={`w-8 h-8 border flex items-center justify-center font-display text-sm shrink-0 ${
              selected === i ? "border-[#D8A936] text-[#D8A936]" : "border-[#343437] text-[#A9A9A6]"
            }`}>
              {String.fromCharCode(65 + i)}
            </span>
            <span className="text-[15px] leading-relaxed">{opt}</span>
          </button>
        ))}
      </div>
      {onBack && (
        <button
          onClick={onBack}
          data-testid="diagnostic-back"
          className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#A9A9A6] hover:text-[#D8A936] transition-colors"
        >
          <ArrowLeft size={14} /> Previous question
        </button>
      )}
    </motion.div>
  );
}

function CaptureScreen({ form, onChange, onSubmit, onSkip, submitting, error }) {
  return (
    <div data-testid="diagnostic-capture">
      <p className="eyebrow">Almost done</p>
      <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight tracking-tight text-[#FAF9F6]">
        Want the full result emailed to you?
      </h2>
      <p className="mt-4 text-[#A9A9A6] max-w-lg leading-relaxed">
        Optional — you can also skip and see the result on-screen only. If you enter an email, JJ will get
        a copy too and may reach out with a scoped recommendation.
      </p>
      <div className="mt-8 space-y-4">
        <label className="block">
          <span className="eyebrow block mb-2">Full name</span>
          <input type="text" value={form.full_name} onChange={(e) => onChange({ ...form, full_name: e.target.value })}
                 className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-4 py-3 outline-none focus:border-[#D8A936]"
                 data-testid="diagnostic-name" />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2">Email</span>
          <input type="email" value={form.email} onChange={(e) => onChange({ ...form, email: e.target.value })}
                 className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-4 py-3 outline-none focus:border-[#D8A936]"
                 data-testid="diagnostic-email" />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2">Organisation</span>
          <input type="text" value={form.organisation} onChange={(e) => onChange({ ...form, organisation: e.target.value })}
                 className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-4 py-3 outline-none focus:border-[#D8A936]"
                 data-testid="diagnostic-org" />
        </label>
      </div>
      {error && <p className="mt-4 text-red-400 text-sm" data-testid="diagnostic-error">{error}</p>}
      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={onSubmit}
          disabled={submitting || !form.email}
          data-testid="diagnostic-submit"
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Scoring…" : "Get my result"} <ArrowRight size={14} />
        </button>
        <button
          onClick={onSkip}
          disabled={submitting}
          data-testid="diagnostic-skip"
          className="btn-ghost"
        >
          Skip — just show me the score
        </button>
      </div>
    </div>
  );
}

function ResultScreen({ result, onRestart }) {
  return (
    <div data-testid="diagnostic-result">
      <p className="eyebrow">Your result</p>
      <div className="mt-6 flex items-baseline gap-6 flex-wrap">
        <span className="oversize text-[80px] md:text-[110px] text-[#D8A936] leading-none">
          {result.score}<span className="text-[#A9A9A6] text-4xl">/{result.max_score}</span>
        </span>
      </div>
      <h2 className="mt-6 font-display text-4xl md:text-5xl leading-tight tracking-tight text-[#FAF9F6]">
        {result.tier}
      </h2>
      <p className="mt-6 text-lg leading-relaxed text-[#A9A9A6] max-w-2xl">{result.summary}</p>

      <div className="mt-10 border border-[#343437] p-6 lg:p-8">
        <p className="eyebrow">Three recommended actions</p>
        <ol className="mt-5 space-y-4">
          {result.actions.map((a, i) => (
            <li key={a} className="flex items-start gap-4 text-[#FAF9F6]" data-testid={`diagnostic-action-${i}`}>
              <span className="w-7 h-7 border border-[#D8A936] text-[#D8A936] flex items-center justify-center text-xs font-display shrink-0">
                {i + 1}
              </span>
              <span className="leading-relaxed">{a}</span>
            </li>
          ))}
        </ol>
      </div>

      <p className="mt-8 text-xs text-[#A9A9A6] italic max-w-2xl">
        This score is an initial indication — not a formal professional assessment. A live audit would map
        specific workflows, dependencies and risks against your context.
      </p>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to={`/services#${result.service_slug || "resilience-audit"}`}
          data-testid="diagnostic-cta-service"
          className="btn-primary"
        >
          See the relevant service <ArrowUpRight size={14} />
        </Link>
        <Link to="/contact" data-testid="diagnostic-cta-contact" className="btn-ghost">
          Talk to JJ <ArrowUpRight size={14} />
        </Link>
        <button onClick={onRestart} data-testid="diagnostic-restart" className="btn-ghost">
          <RefreshCw size={14} /> Retake
        </button>
      </div>
    </div>
  );
}
